import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MdAdd, MdOutlineArrowBack, MdOutlineChatBubbleOutline } from 'react-icons/md';
import axios from 'config/axios';
import { useAppSelector } from 'hooks/useStore';
import { useFillAppColleges } from 'hooks/useFillAppColleges';
import type { College } from 'global/colleges';
import {
    type EssaySummary,
    type DraftState,
    COMMON_APP_PROMPTS,
    formatRelative,
    promptNumber,
    draftState,
} from 'global/essays';

const STATE_LABEL: Record<DraftState, string> = {
    editing: 'Editing', in_review: 'In review', reviewed: 'Reviewed',
};

import './index.css';

// The essays grid + create modal, shared by the standalone Essays page
// (variant='page') and the College Apps dashboard (variant='embed') so both stay in
// sync and look identical. The .essays wrapper scopes the design tokens either way.
function EssaysSection({ variant = 'page' }: { variant?: 'page' | 'embed' }) {
    const navigate = useNavigate();
    const { organization } = useAppSelector(state => state.organization);

    const [essays, setEssays] = useState<EssaySummary[] | null>(null);
    const [loadError, setLoadError] = useState<string | null>(null);

    const [showCreate, setShowCreate] = useState(false);
    const [newTitle, setNewTitle] = useState('');
    const [promptIndex, setPromptIndex] = useState<number | null>(null);
    // Supplement branch: the student's own prompt until we have colleges'
    // official supplement prompts, plus an optional college link + word limit.
    const [essayKind, setEssayKind] = useState<'personal' | 'supplement'>('personal');
    const [customPrompt, setCustomPrompt] = useState('');
    const [collegeQuery, setCollegeQuery] = useState('');
    const [collegeOpen, setCollegeOpen] = useState(false);
    const [pickedCollege, setPickedCollege] = useState<{ id: string; name: string } | null>(null);
    const [wordLimitStr, setWordLimitStr] = useState('');
    const [creating, setCreating] = useState(false);
    const [createError, setCreateError] = useState<string | null>(null);

    const { grid } = useFillAppColleges();
    const collegeMatches = React.useMemo(() => {
        const q = collegeQuery.trim().toLowerCase();
        if (!q || !grid) return [] as College[];
        return grid.colleges.filter(c => c.name.toLowerCase().includes(q)).slice(0, 8);
    }, [collegeQuery, grid]);

    useEffect(() => {
        if (!organization.urlSlug) return;
        let cancelled = false;
        axios.get(`fillapp/student/essays/${organization.urlSlug}/`)
            .then(res => { if (!cancelled) setEssays(res.data.essays || []); })
            .catch(err => { if (!cancelled) setLoadError(err?.response?.data?.message || 'Failed to load essays.'); });
        return () => { cancelled = true; };
    }, [organization.urlSlug]);

    // Each section's "New" card fixes the essay type for the modal.
    const openCreate = (kind: 'personal' | 'supplement' = 'personal') => {
        setNewTitle('');
        setPromptIndex(null);
        setEssayKind(kind);
        setCustomPrompt('');
        setCollegeQuery('');
        setCollegeOpen(false);
        setPickedCollege(null);
        setWordLimitStr('');
        setCreateError(null);
        setShowCreate(true);
    };

    const canCreate = newTitle.trim() !== '' && (
        essayKind === 'personal' ? promptIndex !== null : customPrompt.trim() !== ''
    );

    const createEssay = () => {
        if (creating || !canCreate) return;
        setCreating(true);
        setCreateError(null);
        const body = essayKind === 'personal'
            ? {
                title: newTitle.trim(),
                prompt: COMMON_APP_PROMPTS[promptIndex as number],
            }
            : {
                title: newTitle.trim(),
                essayType: 'supplement',
                prompt: customPrompt.trim(),
                collegeId: pickedCollege?.id || undefined,
                wordLimit: wordLimitStr.trim() ? Number(wordLimitStr) : undefined,
            };
        axios.post(`fillapp/student/essays/${organization.urlSlug}/create/`, body)
            .then(res => navigate(`/college-apps/essays/${res.data.essay.id}`))
            .catch(err => {
                setCreateError(err?.response?.data?.message || 'Could not create essay.');
                setCreating(false);
            });
    };

    const heading = variant === 'page' ? (
        <div className='essays__head'>
            <div>
                <div className='essays__eyebrow'>Common Application</div>
                <h1 className='essays__title'>Your essays</h1>
                <p className='essays__subtitle'>
                    Draft your personal statement and supplements. Keep as many drafts as you like —
                    when one's ready, submit it for your tutor to review.
                </p>
            </div>
        </div>
    ) : (
        <div className='essays__embedHead'>
            <h2>Essay Review</h2>
            <span>Draft your essays and submit one for your tutor to review.</span>
        </div>
    );

    const wrapClass = variant === 'page' ? 'essays layout' : 'essays essays--embed';

    return (
        <div className={wrapClass}>
            {variant === 'page' && (
                <button className='pageBack' type='button' onClick={() => navigate('/college-apps?tab=essays')}>
                    <MdOutlineArrowBack />
                    Back to dashboard
                </button>
            )}

            {heading}

            {loadError ? (
                <div className='essays__error'>{loadError}</div>
            ) : essays === null ? (
                <p className='essays__loading'>Loading your essays…</p>
            ) : (
                <>
                    {([
                        ['personal', 'Personal Statement', 'New personal essay'],
                        ['supplement', 'Supplemental Essays', 'New supplement'],
                    ] as const).map(([kind, groupLabel, newLabel]) => (
                        <div key={kind} className='essays__group'>
                            <h3 className='essays__groupHead'>{groupLabel}</h3>
                            <div className='essays__grid'>
                                {essays.filter(e => (e.essayType === 'supplement') === (kind === 'supplement')).map(essay => {
                                    const drafts = essay.versions || [];
                                    const latest = drafts[drafts.length - 1];
                                    const isSupp = essay.essayType === 'supplement';
                                    const num = isSupp ? null : promptNumber(essay.prompt);
                                    // Published (student-visible) tutor comments across the essay's drafts.
                                    const tutorComments = drafts.reduce((n, v) => n + (v.commentCount || 0), 0);
                                    const latestState = latest ? draftState(latest) : null;
                                    return (
                                        <button
                                            key={essay.id}
                                            type='button'
                                            className='essays__card'
                                            onClick={() => navigate(`/college-apps/essays/${essay.id}`)}
                                        >
                                            <div className='essays__cardTop'>
                                                <span className='essays__cardNum'>
                                                    {isSupp ? 'SUPPLEMENT' : num ? `PROMPT ${num}` : 'ESSAY'}
                                                </span>
                                                {latestState && (
                                                    <span className={`essays__stateChip essays__stateChip--${latestState}`}>
                                                        {STATE_LABEL[latestState]}
                                                    </span>
                                                )}
                                            </div>
                                            {isSupp && essay.collegeName && (
                                                <div className='essays__cardFor'>{essay.collegeName}</div>
                                            )}
                                            <div className='essays__cardTitle'>{essay.title}</div>
                                            {essay.prompt && <p className='essays__cardPrompt'>{essay.prompt}</p>}
                                            <div className='essays__cardMeta'>
                                                <span><b>{drafts.length}</b> draft{drafts.length === 1 ? '' : 's'}</span>
                                                {latest && <span><b>{latest.wordCount}</b> words</span>}
                                                {tutorComments > 0 && (
                                                    <span className='essays__cardComments'>
                                                        <MdOutlineChatBubbleOutline size={12} /> {tutorComments}
                                                    </span>
                                                )}
                                                <span>edited {formatRelative(essay.modifiedAt)}</span>
                                            </div>
                                        </button>
                                    );
                                })}

                                <button type='button' className='essays__card essays__card--new' onClick={() => openCreate(kind)}>
                                    <span className='essays__plus'><MdAdd /></span>
                                    <span className='essays__newLabel'>{newLabel}</span>
                                </button>
                            </div>
                        </div>
                    ))}
                </>
            )}

            {showCreate && (
                <div className='essays__backdrop' onClick={() => !creating && setShowCreate(false)}>
                    <div className='essays__modal' onClick={e => e.stopPropagation()}>
                        <div className='essays__modalHead'>
                            <h2>{essayKind === 'personal' ? 'New personal essay' : 'New supplement'}</h2>
                            <p>
                                {essayKind === 'personal'
                                    ? "Name it, then choose the Common App prompt you're answering."
                                    : "Name it, then enter the college's essay question."}
                            </p>
                        </div>
                        <div className='essays__modalBody'>
                            <div className='essays__field'>
                                <label htmlFor='essay-title'>
                                    Essay title <span className='essays__fieldHint'>— for your own reference</span>
                                </label>
                                <input
                                    id='essay-title'
                                    type='text'
                                    value={newTitle}
                                    onChange={e => setNewTitle(e.target.value)}
                                    placeholder={essayKind === 'personal' ? 'Personal Statement' : 'Why Cornell — Engineering'}
                                    autoFocus
                                />
                            </div>
                            {essayKind === 'personal' ? (
                                <div className='essays__field'>
                                    <label>Which prompt are you answering?</label>
                                    <div className='essays__prompts'>
                                        {COMMON_APP_PROMPTS.map((p, i) => (
                                            <label key={i} className='essays__opt'>
                                                <input
                                                    type='radio'
                                                    name='prompt'
                                                    checked={promptIndex === i}
                                                    onChange={() => setPromptIndex(i)}
                                                />
                                                <span className='essays__optNum'>{i + 1}</span>
                                                <span className='essays__optText'>{p}</span>
                                                <span className='essays__radio' />
                                            </label>
                                        ))}
                                    </div>
                                </div>
                            ) : (
                                <>
                                    <div className='essays__field'>
                                        <label htmlFor='essay-prompt'>
                                            Essay question / prompt <span className='essays__fieldHint'>— copy it from the college's application</span>
                                        </label>
                                        <textarea
                                            id='essay-prompt'
                                            className='essays__promptInput'
                                            value={customPrompt}
                                            onChange={e => setCustomPrompt(e.target.value)}
                                            placeholder='e.g. "What is it about Cornell Engineering that you would like to explore, and how would you make use of our resources?"'
                                            rows={3}
                                        />
                                    </div>
                                    <div className='essays__field'>
                                        <label>
                                            College <span className='essays__fieldHint'>— optional</span>
                                        </label>
                                        {pickedCollege ? (
                                            <div className='essays__collegeChip'>
                                                <span>{pickedCollege.name}</span>
                                                <button type='button' onClick={() => setPickedCollege(null)} aria-label='Remove college'>×</button>
                                            </div>
                                        ) : (
                                            <div className='essays__collegeSearch'>
                                                <input
                                                    type='text'
                                                    value={collegeQuery}
                                                    onChange={e => { setCollegeQuery(e.target.value); setCollegeOpen(true); }}
                                                    onFocus={() => setCollegeOpen(true)}
                                                    onBlur={() => setTimeout(() => setCollegeOpen(false), 150)}
                                                    placeholder='Search Common App colleges…'
                                                />
                                                {collegeOpen && collegeMatches.length > 0 && (
                                                    <div className='essays__collegeResults'>
                                                        {collegeMatches.map(c => (
                                                            <button
                                                                key={c.id}
                                                                type='button'
                                                                onMouseDown={e => e.preventDefault()}
                                                                onClick={() => {
                                                                    setPickedCollege({ id: c.id, name: c.name });
                                                                    setCollegeQuery('');
                                                                    setCollegeOpen(false);
                                                                }}
                                                            >
                                                                {c.name}
                                                            </button>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                    <div className='essays__field essays__field--narrow'>
                                        <label htmlFor='essay-wordlimit'>
                                            Word limit <span className='essays__fieldHint'>— optional; you can write over it</span>
                                        </label>
                                        {/* text + digit filter rather than type='number': a number
                                            input still lets '-', 'e' and unlimited digits through.
                                            4 digits, digits only; the server enforces 1-5000. */}
                                        <input
                                            id='essay-wordlimit'
                                            type='text'
                                            inputMode='numeric'
                                            value={wordLimitStr}
                                            onChange={e => setWordLimitStr(e.target.value.replace(/\D/g, '').slice(0, 4))}
                                            placeholder='250'
                                        />
                                    </div>
                                </>
                            )}
                            {createError && <div className='essays__modalError'>{createError}</div>}
                        </div>
                        <div className='essays__modalFoot'>
                            <button className='essays__btn essays__btn--ghost' onClick={() => setShowCreate(false)} disabled={creating}>
                                Cancel
                            </button>
                            <button
                                className='essays__btn essays__btn--primary'
                                onClick={createEssay}
                                disabled={creating || !canCreate}
                            >
                                {creating ? 'Creating…' : 'Create essay'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default EssaysSection;
